// frontend/src/pages/public/Home/HomePage.tsx
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import SEO from "../../../components/SEO/SEO";
import PublicHeader from "../../../components/PublicHeader/PublicHeader";
import PublicFooter from "../../../components/PublicFooter/PublicFooter";
import Statistics from "../../../components/Statistics/Statistics";
import FAQ from "../../../components/FAQ/FAQ";
import "./HomePage.css";

const CheckIcon: React.FC = () => (
  <svg className="ocf-home-fitIcon" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
  </svg>
);

const XIcon: React.FC = () => (
  <svg className="ocf-home-fitIcon" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const ArrowIcon: React.FC = () => (
  <svg className="ocf-home-ctaArrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
  </svg>
);

const HomePage: React.FC = () => {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [videoError, setVideoError] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    setPrefersReducedMotion(mediaQuery.matches);

    const handleChange = (e: MediaQueryListEvent) => {
      setPrefersReducedMotion(e.matches);
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  const showVideo = !prefersReducedMotion && !videoError;

  const forPeople = [
    "Do not currently have a family doctor",
    "Have moved or lost access to their previous doctor",
    "Are looking for ongoing primary care",
  ];

  const notForPeople = [
    "Walk-in or urgent medical needs",
    "Specialist referrals",
    "Second opinions or one-time visits",
  ];

  return (
    <div className="ocf-home">
      <SEO
        title="Find a Family Doctor in Ontario"
        description="AcceptingMD helps Ontario residents find family doctors accepting new patients, submit intake requests, and connect with participating clinics."
      />
      <PublicHeader variant="solid" />

      {/* HERO */}
      <header className="ocf-home-hero-video" role="banner">
        {showVideo && (
          <video
            className="ocf-home-hero-video-bg"
            autoPlay
            muted
            loop
            playsInline
            poster="/assets/videos/hero-poster.jpg"
            onError={() => setVideoError(true)}
          >
            <source src="/assets/videos/hero-video.mp4" type="video/mp4" />
          </video>
        )}

        <div className="ocf-home-hero-overlay" />

        <div className="ocf-home-hero-content">
          <div className="ocf-home-hero-badge">For Ontario residents</div>
          <h1 className="ocf-home-hero-title">
            Find your family doctor
          </h1>
          <p className="ocf-home-hero-subtitle">
            A free, transparent way to connect with family doctors accepting new patients.
          </p>
          <Link to="/public/find-family-doctor" className="ocf-home-hero-cta">
            Find a Family Doctor
            <ArrowIcon />
          </Link>
          <p className="ocf-home-hero-note">
            No account required · No medical records required to submit a request
          </p>
        </div>
      </header>

      <main className="ocf-home-main">
        {/* STATISTICS */}
        <Statistics />

        {/* IS THIS SERVICE RIGHT FOR YOU? */}
        <section className="ocf-home-fit" aria-label="Is this service right for you">
          <div className="ocf-home-fit-inner">
            <h2 className="ocf-home-fitTitle">Is this service right for you?</h2>

            <div className="ocf-home-fitGrid">
              <div className="ocf-home-fitCard">
                <div className="ocf-home-fitCardTitle">This service is for people who:</div>
                <ul className="ocf-home-fitList" aria-label="People this service is designed for">
                  {forPeople.map((item, index) => (
                    <li key={index} className="ocf-home-fitItem">
                      <span className="ocf-home-fitIconWrap ocf-home-fitIconWrap--green">
                        <CheckIcon />
                      </span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="ocf-home-fitCard">
                <div className="ocf-home-fitCardTitle">This service is not for:</div>
                <ul className="ocf-home-fitList" aria-label="People this service is not designed for">
                  {notForPeople.map((item, index) => (
                    <li key={index} className="ocf-home-fitItem">
                      <span className="ocf-home-fitIconWrap ocf-home-fitIconWrap--red">
                        <XIcon />
                      </span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section className="ocf-home-section" aria-label="How the process works">
          <div className="ocf-home-section-inner">
            <div className="ocf-home-sectionGrid">
              <div className="ocf-home-mapWrap" aria-hidden="true">
                <div className="ocf-home-mapContainer">
                  <div className="ocf-home-mapFade" />
                  <img
                    src="/ontario-map.png"
                    alt=""
                    className="ocf-home-mapImage"
                    loading="lazy"
                  />
                </div>
              </div>

              <div className="ocf-home-sectionContent">
                <h2 className="ocf-home-sectionTitle">4 simple steps to submit a request</h2>

                <ol className="ocf-home-steps" aria-label="Steps to connect with a family doctor">
                  <li className="ocf-home-step">
                    <div className="ocf-home-stepNum">1</div>
                    <div className="ocf-home-stepText">
                      <div className="ocf-home-stepLabel">Patient</div>
                      <div>Submit a request</div>
                    </div>
                  </li>

                  <li className="ocf-home-step">
                    <div className="ocf-home-stepNum">2</div>
                    <div className="ocf-home-stepText">
                      <div className="ocf-home-stepLabel">Clinic</div>
                      <div>Receive and review request</div>
                    </div>
                  </li>

                  <li className="ocf-home-step">
                    <div className="ocf-home-stepNum">3</div>
                    <div className="ocf-home-stepText">
                      <div className="ocf-home-stepLabel">Patient</div>
                      <div>Receive an update from the clinic by email</div>
                    </div>
                  </li>

                  <li className="ocf-home-step">
                    <div className="ocf-home-stepNum">4</div>
                    <div className="ocf-home-stepText">
                      <div className="ocf-home-stepLabel">Patient</div>
                      <div>If invited, arrange your Meet & Greet</div>
                      <div className="ocf-home-stepSub">The clinic will contact you with next steps</div>
                    </div>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <FAQ />

        {/* TRUST & GOVERNANCE */}
        <section className="ocf-home-trust" aria-label="Trust and governance">
          <div className="ocf-home-trust-inner">
            <h2 className="ocf-home-trustTitle">A platform you can trust</h2>

            <p className="ocf-home-trustText">
              AcceptingMD supports a fair, clinic-led approach to patient attachment.
              Clinics remain fully responsible for reviewing requests and making their own intake decisions.
            </p>

            <p className="ocf-home-trustText">
              Patient information is kept minimal and handled securely.
            </p>
          </div>
        </section>

        {/* FINAL CTA */}
        <section className="ocf-home-finalCta" aria-label="Get started">
          <div className="ocf-home-finalCta-inner">
            <h2 className="ocf-home-finalCtaTitle">Ready to look for a family doctor?</h2>
            <p className="ocf-home-finalCtaText">
              Browse doctors near you and submit a request when you find a good fit.
            </p>
            <Link to="/public/find-family-doctor" className="ocf-home-finalCtaBtn">
              Get Started
              <ArrowIcon />
            </Link>
          </div>
        </section>

        {/* CONTACT CTA */}
        <section className="ocf-home-contact" aria-label="Contact us">
          <div className="ocf-home-contact-inner">
            <h2 className="ocf-home-contactTitle">Have questions?</h2>
            <p className="ocf-home-contactText">
              We're here to help. Reach out to our team for assistance.
            </p>
            <Link to="/public/contact" className="ocf-home-contactBtn">
              Contact Us
            </Link>
          </div>
        </section>

        {/* COMMUNITY NAVIGATOR */}
        <section className="ocf-home-trust" aria-label="Community Navigator assistance">
          <div className="ocf-home-trust-inner">
            <h2 className="ocf-home-trustTitle">
              Having difficulty using the online request?
            </h2>

            <p className="ocf-home-trustText">
              Community Navigator help is available in participating Ontario Health Team (OHT) regions for people who need assistance using AcceptingMD.
            </p>

            <Link to="/public/community-navigator-help" className="ocf-home-contactBtn">
              Get Community Navigator help
            </Link>
          </div>
        </section>

        {/* Footnote */}
        <section className="ocf-home-footnote" aria-label="Important note">
          <div className="ocf-home-footnote-inner">
            * Submitting a request does not guarantee acceptance. Clinics review requests
            independently based on their current capacity and practice scope.
          </div>
        </section>

        <PublicFooter />
      </main>
    </div>
  );
};

export default HomePage;